import * as Sentry from '@sentry/react';
import { DEFAULT_APPOINTMENT_TYPE, getLeadIDFromURL, setLeadIDToURL } from 'domains/signup/data/queryParams';
import { TherapyTarget } from 'domains/signup/data/types';
import { getRedirectAfterSignupV4Complete } from 'domains/signupv4/Insurance/external-forms/redirect';
import { insuranceMachineV4, InsuranceResult } from 'domains/signupv4/Insurance/insuranceMachineV4';
import { SignupLeadProgressV4 } from 'domains/signupv4/Leads/types';
import { miniScreenerMachine, MiniScreenerResult } from 'domains/signupv4/Mini-Screener/miniScreenerMachine';
import {
  loadLeadDataLogic,
  updateLead as updateLeadLogic,
  upsertInsuranceLeadLogic,
} from 'domains/signupv4/Parent/actors';
import { getSignupResumeCheckGuard } from 'domains/signupv4/Parent/resume';
import { scheduleMachineV4, ScheduleResult } from 'domains/signupv4/schedule/scheduleMachineV4';
import { isString } from 'lodash';
import { MachineStateType } from 'utils/xstate/types';
import { assign, setup } from 'xstate';
import { CreateLeadResponseV4 } from '../Leads/queries';
import { createSalesforceLead } from 'domains/signupv4/Leads/queries';
import { getUTMParameters } from 'domains/tracking/UTMTracking';
import { getGCLIDParameters } from 'domains/tracking/GCLIDtracking';

type ScheduleParameters = {
  appointmentType: string;
  calendarID?: string;
};

// Machine's input
export type SignupMachineInput = {
  leadID?: string;
} & Partial<ScheduleParameters>;

const DEFAULT_CONFIG: SignupMachineInput = {
  appointmentType: DEFAULT_APPOINTMENT_TYPE,
};

export type CompletedSignupV4Data = {
  state: string;
  firstName: string;
  lastName: string;
  email: string;
  receiveNewsletter: boolean;
  phoneNumber: string;
  target: TherapyTarget;
  miniScreenerInfo: MiniScreenerResult['data'];
  insuranceInfo: InsuranceResult['data'];
  scheduleInfo: ScheduleResult['data'];
  leadID: string;
  schedulingID: string;
  scheduleParameters: ScheduleParameters;
  insuranceLeadID?: string;
  errorMessage?: string;
};

export type IncompleteSignupV4Data = Partial<CompletedSignupV4Data>;

type InternalContext = Partial<CompletedSignupV4Data> & {
  machineConfig: SignupMachineInput;

  /** The step which the machine resumed to */
  resumedStep?: string;
  progressData?: SignupLeadProgressV4;
  pendingLeadUpdate?: boolean;
};

export const signupMachineV4 = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'submitState'; state: string }
      | { type: 'submitName'; firstName: string; lastName: string }
      | { type: 'submitEmail'; email: string; receiveNewsletter: boolean }
      | { type: 'submitPhoneNumber'; phoneNumber: string }
      | { type: 'submitTarget'; target: TherapyTarget }
      | { type: 'enterRunScreener'; runScreener: boolean }
      | { type: 'leadCreated'; output: CreateLeadResponseV4 }
      | { type: 'leadCreationFailed' }
      | { type: 'pendingLeadUpdate'; pendingLeadUpdate: boolean },
    input: {} as SignupMachineInput,
  },

  actions: {
    redirectOnComplete: ({ context }) => {
      const { invitationLink } = context.scheduleInfo?.scheduleResponse ?? {};

      if (invitationLink) {
        window.location.replace(invitationLink);
      } else {
        window.location.replace(getRedirectAfterSignupV4Complete(context as CompletedSignupV4Data));
      }
    },

    saveLeadData: assign((_, { leadProgressData }: { leadProgressData: SignupLeadProgressV4 | null }) => {
      if (leadProgressData) {
        return {
          schedulingID: leadProgressData.schedulingId,
          progressData: leadProgressData,

          /** When resuming after communicationConcerns, assume adult as target, needed in translation strings */
          target: leadProgressData.communicationConcerns ? ('adult' as TherapyTarget) : undefined,

          /** When resuming past insurance questions, assume no insurance */
          insuranceInfo: leadProgressData.desiredPaymentMethod
            ? ({ useInsurance: false } as InsuranceResult['data'])
            : undefined,
        };
      }
      return {};
    }),

    saveState: assign((_, params: { state: string }) => ({
      state: params.state,
    })),
    saveName: assign((_, params: { firstName: string; lastName: string }) => ({
      firstName: params.firstName,
      lastName: params.lastName,
    })),
    saveEmail: assign((_, params: { email: string; receiveNewsletter: boolean }) => ({
      email: params.email,
      receiveNewsletter: params.receiveNewsletter,
    })),

    savePhoneNumber: assign((_, params: { phoneNumber: string }) => ({
      phoneNumber: params.phoneNumber,
    })),

    saveTarget: assign((_, params: { target: TherapyTarget }) => ({
      target: params.target,
    })),

    saveMiniScreenerInfo: assign((_, params: { miniScreenerInfo: MiniScreenerResult }) => ({
      miniScreenerInfo: params.miniScreenerInfo.data,
    })),

    saveInsuranceInfo: assign((_, params: { insuranceInfo: InsuranceResult }) => ({
      insuranceInfo: params.insuranceInfo.data,
    })),

    saveScheduleInfo: assign((_, params: { scheduleInfo: ScheduleResult['data'] }) => ({
      scheduleInfo: params.scheduleInfo,
    })),

    saveLeadID: assign((_, params: { leadID: string; schedulingID: string }) => ({
      leadID: params.leadID,
      schedulingID: params.schedulingID,
    })),

    saveInsuranceLeadID: assign((_, params: { insuranceLeadID: string }) => ({
      insuranceLeadID: params.insuranceLeadID,
    })),

    saveResumedStep: assign((_, params: { resumedStep: string }) => ({
      resumedStep: params.resumedStep,
    })),

    saveLeadIDToURL: (_, params: { leadID: string }) => {
      setLeadIDToURL(params.leadID);
    },

    clearLeadID: assign(() => ({
      leadID: undefined,
    })),

    createLead: ({ context, self }) => {
      const _context = context as CompletedSignupV4Data;
      const UTMParams = getUTMParameters();
      const GCLIDParams = getGCLIDParameters();
      const payload = {
        address: {
          state: _context.state,
        },
        email: _context.email,
        firstName: _context.firstName,
        lastName: _context.lastName,
        phone: _context.phoneNumber,

        ...UTMParams,
        ...GCLIDParams,
      };

      createSalesforceLead(payload)
        .then(response => {
          self.send({
            type: 'leadCreated',
            output: response,
          });
        })
        .catch(error => {
          Sentry.captureException('Error while creating the Lead with Salesforce service', {
            extra: {
              error,
              payload,
            },
          });

          self.send({ type: 'leadCreationFailed' });
        });
    },

    setPendingLeadUpdate: assign((_, params: { pendingLeadUpdate: boolean }) => ({
      pendingLeadUpdate: params.pendingLeadUpdate,
    })),

    clearPendingLeadUpdate: ({ context, self }) => {
      if (context.pendingLeadUpdate) {
        updateLeadLogic(context);
      }

      self.send({ type: 'pendingLeadUpdate', pendingLeadUpdate: false });
    },

    updateLead: ({ context, self }) => {
      // When the lead is not created yet, so we can't update it
      if (!isString(context.leadID)) {
        self.send({ type: 'pendingLeadUpdate', pendingLeadUpdate: true });
        return;
      }

      updateLeadLogic(context);
    },

    createInsuranceLead: ({ context }) => {
      updateLeadLogic(context);
    },
  },
  actors: {
    loadLeadData: loadLeadDataLogic,
    upsertInsuranceLead: upsertInsuranceLeadLogic,

    runMiniScreenerMachine: miniScreenerMachine,
    runInsuranceMachine: insuranceMachineV4,
    runScheduleMachine: scheduleMachineV4,
  },
  guards: {
    hasLeadID: ({ context }) => isString(context.leadID),

    exitedByBack: (_, params: { earlyExit: boolean }) => params.earlyExit,

    canResumeToEnterTarget: getSignupResumeCheckGuard('phone'),
    canResumeToEnteringInsurance: getSignupResumeCheckGuard('communicationConcerns'),
    canResumeToEnteringSchedule: getSignupResumeCheckGuard('desiredPaymentMethod'),
    canDoEligibilityCheck: ({ context }) => context.insuranceInfo?.insuranceForm.notInsuranceAvailable === false,

    isInsurance: (_, params: { useInsurance: boolean }) => params.useInsurance === true,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5SwJZQHYFcAOBZAhgMYAWK6YAagCwB0sALvgE70DEA2gAwC6io2Ae1T0UA9HxAAPRAEYAzJxqcFAThUK5MgEwBWLQA4dAGhABPRAbk0dKnXv2ctANnVyqAX3cnUGHARJklLQMzGzsMrxIIILCouJR0gjyisqcahra9sZmsjpOTjQKDnLqOo6Gcp7eaFh4RKTk1DQANgL4EGRQADJg7QAi+IysEGJgNGQAbgIA1mOt7T39g-hckfxCKCJiEolOMvo0WipObloA7HppWlQm5gjOKXlUZZwOznJaVSA+tf4NQTQmHBMABbToABSYAigQNgsA4PAkMU2cR2sjkdhoTjs+jkmn0VE4yjkt0Q+hk1iJnCoMnyVBU+iOZy+Pz89UCTVhoIhUJhcHh4TW0Q2W3ioES8kx2J0uPxhOJpKSZysnDOjKozhUMguZ21LJqbICjVoXLB6CgkOhsIFWiFyNFaKSGJ0WJxeP28rxiuctGxTioVDkuoq8ic+t8dSNANNPKt-I4cjtItRCXRUrdcqJXpyCAxBS0bn9VDOzwUdjDXm+Bsj-yaYHQ9DATAAyoxG6xYJgAEZg+itwZgVZI5PbVP3DVWJy6s7Yzg6GQyLU3HPyYuHI5OfRnFSqs57ivVCN-Dm0euNpgAOXwILAHe7vavN6HUXtKfFuRUZyUMj0eWd+nyEkVzcWgzg3Lcdz3fdw1+dljRoM8m0fW8uyIaZn3WWJR3fe4tEcJQqDpPRnGubVFRkAMrAUbdixUGlt0cGDDVrU8GybABREF8BQZo7x7TZOO45oMOFLCxSkRBtwOGw5CcPCtF0PI53I0512OCDd2gytWRrE8ELYphBJ41hUMIdDERfEdxIlZ1XRld0CSzIC7gowkaAcGiaO1HdPm06tj3gxCmHBYhRgvUEuybEy0JE19sIkhA9gODdTguPCVGucjAwOai6K8hjfMPWCozrAyQrCiKos7fj6HK8hwpBSKmFiqzHSSw5jlSy4MuXFzrgKH8KPOLcg2xQqqyPOCASCuqwAapq+N7Wb5qbQVhzEtr9g6k4NTSq5esQDKKTkhw9E0TgZAu5k-MmkraEIIFBk6RYIGGUZxnQKZZjofypqaB7ehEc0XoQSYBEIJ6xFWFqNrHSUXWlWUPSc8jaRdZRAzyGUd3yA8JuKliaABp7gd6V6myhJgaGwZpBgAMwEJgQR+27CeJoHujJ0HPvByH0GhizMJReKbPTezMwVFcDF9Hc7GOa41F1JjdMCgyABVmBgNhTPMpNYZwtGqOGk5NzKI4ZFRijrFOD493UH9lYC6b1c1sA2Gq3sNaYLWYeF6yPysbRixeNGMv0RUpwpMDtUu3a8LxR2-tY89OlwMgUGbAH6yikZyA+r6xiYTB0DT9AM6z8gmCd32HTh2zEYcz1nMkujCjt+wLqOXRE7u-SU-NUvy6BbOmDevOwe+ouS-TzPh8r6uInWv3HXhuykccyW7jsLQlGlVUSm0ZVxp0p3Sv7qAAEl0E7Jh8HQQhb1zsYJ8L4ur5vu+H+rwXROXuuS3chRMCAYFIKW0IqZ4KhvwYkgfOHQHwdA90JkFTo79MC33vo-d6L9ARv2vugz+YAF56z-jhBS+FqREQUnJeiiodDPCUAWRkn4FAGDyEgvSKDzSZ2IJATAzQsHjx5pPYuPC+ECO-iQ2uZD5IESoSRWhOZ6RfjOiWX8AEMp7g4arc+YiID8MEc-YRr90B6IMcQpe0iEoKCJDQH8exCKvD0FqOhHw7EQSKMoGSeMT5JxZrUAAwgIEENM3a3hrm+axbgDgXXodiWkRI9AQJOHYqgAFcRh2cAuTwlZ0ACAgHACQvi7qWMiYkAAtE4RUlSlBUkXLJK6V19DaIBCEFgpSRaIA1ORHEqT5AKXoWUQkzSboEz0vMDopMliMA6f7e4tgaBHRKJwQChZMo5l0DlZ4OhdSEkxoBFpnJgRmgtLya0syV4LloHIBwdEtR2A+IGb05J3LPH9BRNIcTrpFWYpwgy-ZGwXLHA8Wg9jaQ7iOguHpehKSMguh8GO5xDnJyQteMAQKDbqB3gYfQ5JsbpDIiuTGNBgFqgDLi4OyK+4cS4jxDFCVLrbncoyBQThqQLjogdJIgYrAKWLASNJBILhUpmqFeqlUmD0sSMMrEupdxoznHucimoaABi8aoY4JwqXs2emTKVshdx2OUOSWiOzD7KoYXRakugGSqjnD436vcBHtACY9UU7EmCU31UkBwFJKH5BZayrQyrcRYhxoyAkWgfyMVGb8nRTYvZa29fsC6ciA0FiDajACtT6TvPyh4WNKtnbn0HrPMAI9vWcrXnkRcrwCxcsAoUZRehtnyCVoW0+KKmCoPwRgh+ybfVps3BmlZwacz2G-IYNIxwvEioMp0MxAjvUgrsTWlwVwFwW3HXhRZe4Hi2AcA61mekdJBJCQIwFll9ZRJlIsoMhIbHxy3VvPY6512EVIhREZnggA */
  id: 'signupMachineV4',

  initial: 'start',

  context: ({ input }) => ({
    leadID: getLeadIDFromURL() as string,
    machineConfig: {
      ...DEFAULT_CONFIG,
      ...input,
    },
    scheduleParameters: {
      appointmentType: input.appointmentType ?? DEFAULT_APPOINTMENT_TYPE,
      calendarID: input.calendarID,
    },
  }),

  states: {
    start: {
      always: [
        {
          guard: 'hasLeadID',
          target: 'loadingLeadData',
        },
        {
          target: 'enterState',
        },
      ],
    },

    loadingLeadData: {
      invoke: {
        src: 'loadLeadData',
        id: 'loadLeadData',
        input: ({ context: { leadID } }) => ({ leadID: leadID as string }),
        onDone: {
          target: 'resumingProgress',
          actions: {
            type: 'saveLeadData',
            params: ({ event }) => ({ leadProgressData: event.output }),
          },
        },
      },
    },
    resumingProgress: {
      always: [
        {
          target: 'enteringSchedule',
          guard: {
            type: 'canResumeToEnteringSchedule',
            params: ({ context }) => ({ progress: context.progressData as SignupLeadProgressV4 }),
          },
          actions: {
            type: 'saveResumedStep',
            params: { resumedStep: 'enteringSchedule' },
          },
        },
        {
          target: 'enteringInsurance',
          guard: {
            type: 'canResumeToEnteringInsurance',
            params: ({ context }) => ({ progress: context.progressData as SignupLeadProgressV4 }),
          },
          actions: {
            type: 'saveResumedStep',
            params: { resumedStep: 'enteringInsurance' },
          },
        },
        {
          target: 'enterTarget',
          guard: {
            type: 'canResumeToEnterTarget',
            params: ({ context }) => ({ progress: context.progressData as SignupLeadProgressV4 }),
          },
          actions: {
            type: 'saveResumedStep',
            params: { resumedStep: 'enterTarget' },
          },
        },

        // In case all else fails, partially completed data, etc. Default to start and remove lead ID so it gets created again
        { target: 'enterState', actions: 'clearLeadID' },
      ],
    },

    enterState: {
      on: {
        submitState: [
          {
            target: 'enterName',

            actions: {
              type: 'saveState',
              params: ({ event }) => event,
            },
          },
        ],
      },
    },
    enterName: {
      on: {
        submitName: {
          target: 'enterEmail',

          actions: {
            type: 'saveName',
            params: ({ event }) => event,
          },

          reenter: true,
        },

        back: 'enterState',
      },
    },

    enterEmail: {
      on: {
        submitEmail: [
          {
            target: 'enterPhoneNumber',
            actions: {
              type: 'saveEmail',
              params: ({ event }) => event,
            },
          },
        ],
        back: 'enterName',
      },
    },

    enterPhoneNumber: {
      on: {
        back: 'enterEmail',

        submitPhoneNumber: [
          {
            target: 'enterTarget',
            actions: [
              {
                type: 'savePhoneNumber',
                params: ({ event }) => event,
              },
              'createLead',
            ],
          },
        ],
      },
    },

    leadCreationFailed: {},

    enterTarget: {
      on: {
        back: 'enterPhoneNumber',

        submitTarget: {
          target: 'enteringMiniScreener',
          actions: [
            {
              type: 'saveTarget',
              params: ({ event }) => event,
            },
            'updateLead',
          ],
        },
      },
    },

    enteringMiniScreener: {
      invoke: {
        id: 'runMiniScreenerMachine',
        src: 'runMiniScreenerMachine',

        input: ({ context }) => ({
          target: context.target as TherapyTarget,
          prevState: { state: context.state, ...context.miniScreenerInfo },
          canExitByBack: context.resumedStep !== 'enteringMiniScreener',
        }),

        onDone: [
          {
            target: 'enterTarget',
            guard: {
              type: 'exitedByBack',
              params: ({ event: { output } }) => ({ earlyExit: output.earlyExit ?? false }),
            },
            actions: {
              type: 'saveMiniScreenerInfo',
              params: ({ event }) => ({ miniScreenerInfo: event.output }),
            },
          },

          {
            target: 'enteringInsurance',
            actions: [
              {
                type: 'saveMiniScreenerInfo',
                params: ({ event }) => ({ miniScreenerInfo: event.output }),
              },
              'updateLead',
            ],
          },
        ],
      },
    },

    enteringInsurance: {
      invoke: {
        id: 'runInsuranceMachine',
        src: 'runInsuranceMachine',

        input: ({ context }) => ({
          canExitByBack: context.resumedStep !== 'enteringInsurance',
          prevState: { state: context.state, ...context.insuranceInfo },
        }),

        onDone: [
          {
            target: 'enteringMiniScreener',
            guard: {
              type: 'exitedByBack',
              params: ({ event: { output } }) => ({ earlyExit: output.earlyExit ?? false }),
            },
            actions: [
              {
                type: 'saveInsuranceInfo',
                params: ({ event }) => ({ insuranceInfo: event.output }),
              },
            ],
          },

          {
            target: 'upsertingInsuranceLead',
            guard: {
              type: 'isInsurance',
              params: ({ event: { output } }) => ({ useInsurance: output.data.useInsurance }),
            },
            actions: [
              {
                type: 'saveInsuranceInfo',
                params: ({ event }) => ({ insuranceInfo: event.output }),
              },
              'updateLead',
            ],
          },

          {
            target: 'enteringSchedule',
            actions: [
              {
                type: 'saveInsuranceInfo',
                params: ({ event }) => ({ insuranceInfo: event.output }),
              },
              'updateLead',
            ],
          },
        ],
      },
    },

    upsertingInsuranceLead: {
      invoke: {
        src: 'upsertInsuranceLead',

        input: ({ context }) => context as CompletedSignupV4Data,
        onDone: {
          target: 'enteringSchedule',
          actions: [
            {
              type: 'saveInsuranceLeadID',
              params: ({ event }) => ({ insuranceLeadID: event.output.id }),
            },
          ],
        },
        onError: {
          target: 'upsertingInsuranceLeadFailed',
        },
      },
    },

    upsertingInsuranceLeadFailed: {},

    enteringSchedule: {
      invoke: {
        id: 'runScheduleMachine',
        src: 'runScheduleMachine',

        input: ({ context }) => {
          const completedContext = context as CompletedSignupV4Data;

          return {
            machineConfig: {
              state: completedContext.state,
              firstName: completedContext.firstName,
              lastName: completedContext.lastName,
              email: completedContext.email,
              phoneNumber: completedContext.phoneNumber,
              leadID: completedContext.leadID,
              schedulingID: completedContext.schedulingID,
              target: completedContext.target,

              miniScreenerInfo: completedContext.miniScreenerInfo,

              useInsurance: completedContext.insuranceInfo.useInsurance,
              insuranceEligibility: completedContext.insuranceInfo.insuranceEligibility,
              insuranceForm: completedContext.insuranceInfo.insuranceForm,
              insuranceType: completedContext.insuranceInfo.insuranceType,

              // coming from URL as query params
              appointmentType: context.machineConfig.appointmentType as string,
              calendarID: context.machineConfig.calendarID,

              canExitByBack: context.resumedStep !== 'enteringSchedule',
            },
          };
        },

        onDone: [
          {
            target: 'enteringInsurance',
            guard: {
              type: 'exitedByBack',
              params: ({ event: { output } }) => ({ earlyExit: output.earlyExit ?? false }),
            },
          },
          {
            target: 'signupComplete',
            actions: {
              type: 'saveScheduleInfo',
              params: ({ event }) => ({ scheduleInfo: event.output.data }),
            },
          },
        ],
      },
    },

    signupComplete: {
      always: {
        actions: 'redirectOnComplete',
      },
    },
  },

  on: {
    leadCreated: {
      actions: [
        {
          type: 'saveLeadID',
          params: ({ event }) => ({ leadID: event.output.id, schedulingID: event.output.schedulingId }),
        },
        {
          type: 'saveLeadIDToURL',
          params: ({ event }) => ({ leadID: event.output.id }),
        },
        'clearPendingLeadUpdate',
      ],
    },
    leadCreationFailed: {
      target: '.leadCreationFailed',
    },
    pendingLeadUpdate: {
      actions: [
        {
          type: 'setPendingLeadUpdate',
          params: ({ event }) => ({ pendingLeadUpdate: event.pendingLeadUpdate }),
        },
      ],
    },
  },
});

export type SignupState = MachineStateType<typeof signupMachineV4>;
